import {AfterViewInit, Component, Input, OnDestroy, ViewChild} from '@angular/core';
import {ReplaySubject} from 'rxjs';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthService} from '../../../shared/services/auth.service';
import {delay, distinctUntilChanged, startWith, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {DeregistrationPack} from '../../../shared/interface/deregistration-pack';
import {DeregistrationPackService} from '../../../shared/services/deregistration-pack.service';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {FilterService} from '../../../shared/services/filter.service';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../shared/ngrx/route-serializer';
import {RegistrationIdImport} from "../../../shared/interface/registration-id-import";
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {SelectionModel} from "@angular/cdk/collections";
import {
  DEREGISTRATION_PACK_STATUS_NEW, DICT_TYPE_TEMPLATE_TYPE, DictService, TEMPLATE_TYPE_DECLARATION_FOR_DEREGISTRATION,
} from "../../../shared/services/dict.service";
import {TemplateService} from "../../../shared/services/template.service";

@Component({
  selector: 'app-all-deregistration-pack-list',
  templateUrl: './all-deregistration-pack-list.component.html',
  styleUrls: ['./all-deregistration-pack-list.component.scss']
})
export class AllDeregistrationPackListComponent implements AfterViewInit, OnDestroy {
  @Input() filterPending = false;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  readonly DEREGISTRATION_PACK_STATUS_NEW = DEREGISTRATION_PACK_STATUS_NEW;
  protocolOpened = false;
  selection = new SelectionModel<DeregistrationPack>(true, []);
  initialFilters: any;
  deregistrationPacks: DeregistrationPack[] = [];
  deregistrationPackIds: string[] = [];
  displayedColumns: string[] = [  'select', 'name', 'insuranceCompany', 'company', 'status',
    'createdAt', 'sentAt', 'registrationsCount'];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private deregistrationPackService: DeregistrationPackService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
    private dictService: DictService,
    private templateService: TemplateService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.deregistrationPackService.deregistrationPackListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<DeregistrationPack>) => {
          this.deregistrationPacks = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      insuranceCompany: 'int',
      status: 'int',
      company: 'int',
      gte$createdAt: 'date',
      lte$createdAt: 'date',
      gte$sentAt: 'date',
      lte$sentAt: 'date',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        ...this.filtersForm.value
      })]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.loadListData();
      })
    ;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.deregistrationPackService.loadList(this.paginator.pageIndex, this.paginator.pageSize, this.initialFilters);
  }

  filter(): void {
    if (this.paginator.pageIndex === 0) {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: this.paginator.pageIndex,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value
      })]);
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value,
      })]);
    }
  }

  downloadReport(): void {
    this.deregistrationPackService.downloadReport(this.initialFilters);
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  delete(deregistrationPack: DeregistrationPack): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć paczkę?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deregistrationPackService.deleteDeregistrationPack(deregistrationPack.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto paczkę', '', {duration: 2000});
            this.loadListData();
          });
      }
    });
  }

  openProtocol(): void {
    this.protocolOpened = true;
  }

  cancel(): void {
    this.protocolOpened = false;
    this.selection.clear();
  }

  generateDataDocuments(): void {
    this.deregistrationPackIds = [];
    this.selection.selected.forEach((deregistrationPack: DeregistrationPack) =>{
      this.deregistrationPackIds.push(deregistrationPack.uuid);
    });
    if (this.selection.selected.length > 0) {
      this.dictService.getId(TEMPLATE_TYPE_DECLARATION_FOR_DEREGISTRATION, DICT_TYPE_TEMPLATE_TYPE)
        .subscribe((dictId: number) => {
          this.templateService.generate({
            type: dictId,
            deregistrationPackIds: this.deregistrationPackIds,
          }).subscribe((response: Uuid) => {
            const dialogRef = this.dialog.open(JobResultDialogComponent, {
              disableClose: true,
              width: '500px',
              data: {jobId: response.uuid, showProgress: false},
            });

            dialogRef.afterClosed().subscribe(() => {
              // this.generatingDocument = false;
            });
          }, (error: HttpErrorResponse) => {
            // this.generatingDocument = false;
            this.selection.clear();
            if (error.status === 404) {
              this.snack.open('Brak szablonów dla tego typu dokumentu', '', {duration: 2000});
              return;
            }

            this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
          });
        });
      } else {
        this.snack.open('Nie wybrano żadnego pliku do importu','',{duration: 2000} );
      }
      this.protocolOpened = false;
      this.selection.clear();
    }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.deregistrationPacks.length;
    return numSelected === numRows;
  }
  isSelect(row?: string): boolean {
    let max = false;
    this.selection.selected.forEach((deregistrationPack: DeregistrationPack) => {
      if (row === deregistrationPack.uuid){
        max = true;
      }
    });
    return max;
  }

  checkboxLabel(row?: DeregistrationPack): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.uuid}`;
  }
}
